// GoogleDriveMenu.tsx
import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslations, useSnackbar } from "hooks";
import { getTranslation } from "common";
import useAppservices from "hooks/useAppservices";
import { useGoogleLogin } from "@react-oauth/google";

type SupportedTypes =
  | "document"
  | "spreadsheets"
  | "presentation"
  | "drawing"
  | "folder";

interface GoogleDriveMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onOpenPicker: () => void;
  onOpenFolderPicker: () => void;
  onCreateDocument: (type: SupportedTypes, token?: string | null) => Promise<void>;
}

const GoogleDriveMenu: React.FC<GoogleDriveMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onOpenPicker,
  onOpenFolderPicker,
  onCreateDocument,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const { appservices, handleLinkAppservice } = useAppservices();
  const [menuPhase, setMenuPhase] = useState<"main" | "create">("main");
  const redoGoogleDocRef = React.useRef<SupportedTypes | null>(null);

  const handleOpenCreateMenu = () => setMenuPhase("create");
  const handleBackToMainMenu = () => setMenuPhase("main");

  const failureGoogle = () => {
    console.error("fail");
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (e: any) => {
      const data = await handleLinkAppservice(e, 1);
      const token = data?.token;
      if (redoGoogleDocRef.current && token) {
        await onCreateDocument(redoGoogleDocRef.current, token);
      }
      redoGoogleDocRef.current = null;
    },
    onError: failureGoogle,
    flow: "auth-code",
    enable_serial_consent: false,
    scope:
      "https://www.googleapis.com/auth/drive.file",
  });

  const handleCreateDocument = async (type: SupportedTypes) => {
    if (!appservices[1]) {
      sendSnack({
        type: "error",
        message: getTranslation(translations, "google.auth.failed.for.doc"),
      });
      return;
    }
    await onCreateDocument(type);
  };

  const handleClose = () => {
    setMenuPhase("main");
    onClose();
  };

  return (
    <Menu
      id="google-drive-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{ "aria-labelledby": "google-drive-button" }}
    >
      {menuPhase === "main" ? (
        <div>
          <MenuItem onClick={onOpenPicker}>
            {getTranslation(translations, "pm.startprocess.button.upload.file.drive")}
          </MenuItem>
          <MenuItem onClick={onOpenFolderPicker}>
            {getTranslation(translations, "pm.startprocess.button.upload.folder.drive")}
          </MenuItem>
          <MenuItem onClick={handleOpenCreateMenu}>
            {getTranslation(translations, "google.create.and.upload.file")}
          </MenuItem>
        </div>
      ) : (
        <div>
          {appservices[1] ? (
            <React.Fragment>
              <MenuItem onClick={handleBackToMainMenu}>
                <ArrowBackIcon />
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("document")}>
                {getTranslation(translations, "general.google.doc")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("spreadsheets")}>
                {getTranslation(translations, "general.google.sheet")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("presentation")}>
                {getTranslation(translations, "general.google.presentation")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("drawing")}>
                {getTranslation(translations, "general.google.drawing")}
              </MenuItem>
              <MenuItem onClick={() => handleCreateDocument("folder")}>
                {getTranslation(translations, "general.google.folder")}
              </MenuItem>
            </React.Fragment>
          ) : (
            <Stack sx={{ p: 1, maxWidth: 250 }}>
              <Typography fontSize={13}>
                {getTranslation(translations, "link.flowenti.drive.account")}
              </Typography>
              <Button size="small" variant="contained" onClick={googleLogin}>
                {getTranslation(translations, "link.flowenti.drive.account.button")}
              </Button>
            </Stack>
          )}
        </div>
      )}
    </Menu>
  );
};

export default GoogleDriveMenu;